import { Button, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next';
import MapBox from '../../components/specific/map/MapBox';
import Card from '../../components/common/cards/Card';
import ModalShareMap from '../../components/specific/modal/ModalSharemap';
import { useEffect, useState, useCallback } from 'react';
import { useMarkers } from '../../hooks/useMarker';
import { useUserData } from '../../hooks/useUserData';
import { useToast } from '../../hooks/useToast';
import EmptyState from '../../assets/gifs/empty_state_cards.gif';
import { geocodingService } from '../../services';

const PinMap = () => {
  const { markers } = useMarkers();
  const { userId } = useUserData();
  const { t, i18n } = useTranslation();
  const { genericError } = useToast();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [localizedMarkers, setLocalizedMarkers] = useState<typeof markers>([]);

  useEffect(() => {
    setIsDisabled(markers.length === 0);
  }, [markers]);

  const localizeMarker = useCallback(async (marker: (typeof markers)[number]) => {
    try {
      const result = await geocodingService.localizePlaceName(marker.name, i18n.language);
      return { ...marker, localizedName: result.localizedName };
    } catch (error) {
      console.error('Error in localizeMarker:', error);
      genericError('localizzazione marker');
      return { ...marker, localizedName: marker.name };
    }
  }, [i18n.language, genericError]);

  useEffect(() => {
    // Assicurati che markers sia sempre un array valido
    const validMarkers = Array.isArray(markers) ? markers : [];

    if (validMarkers.length === 0) {
      setLocalizedMarkers([]);
      return;
    }

    // Mostra sempre i primi 6 marker, con i nuovi che sostituiscono i più vecchi
    const displayMarkers = validMarkers.length <= 6
      ? [...validMarkers].reverse() // Se <= 6, mostra tutti in ordine inverso
      : [...validMarkers.slice(-6)].reverse(); // Se > 6, mostra gli ultimi 6 in ordine inverso

    // Per l'inglese non serve localizzazione
    if (i18n.language === 'en') {
      setLocalizedMarkers(displayMarkers);
      return;
    }

    // Localizza i marker per lingue diverse dall'inglese
    const localizeMarkers = async () => {
      try {
        const results = await Promise.all(displayMarkers.map(localizeMarker));
        setLocalizedMarkers(results);
      } catch (error) {
        console.error('Error localizing markers:', error);
        genericError('localizzazione marker multipli');
        setLocalizedMarkers(displayMarkers);
      }
    };

    localizeMarkers();
  }, [markers, i18n.language, localizeMarker, genericError]);

  const disabledStyle = {
    backgroundColor: '#DBDBDB',
    color: '#475467'
  }

  const buttonStyle = {
    cursor: 'pointer',
    backgroundColor: '#E9495A',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#E6263B'
    }
  }

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const handleShareMap = useCallback(() => {
    const mapLink = `${window.location.origin}/profile/${userId}`;
    const staticImageUrl = getSharedMapImageUrl(markers.map(marker => ({
      longitude: marker.longitude,
      latitude: marker.latitude,
    })));

    return { mapLink, staticImageUrl };
  }, [userId, markers]);

  const getStaticMapImageUrl = useCallback((longitude: number, latitude: number) => {
    return geocodingService.generateStaticMapUrl(longitude, latitude, 200, 90, 6);
  }, []);

  const getSharedMapImageUrl = useCallback((markers: { longitude: number; latitude: number }[]) => {
    return geocodingService.generateSharedMapUrl(markers, 1200, 630, 5);
  }, []);


  return (
    <>
      <div className='w-full h-auto flex flex-col justify-center items-center p-6'>
        <div className='flex flex-col items-center mt-4 mb-2 px-4 sm:mt-6 mb-4 text-center'>
          <p className='text-primary font-bold text-sm sm:text-base'>
            Pin Your Places
          </p>
          <Typography variant='h3' className='text-text-primary font-medium text-xl sm:text-2xl md:text-2xl mt-2'>
            {t('pinMap:titleLeft')} <span className='text-primary font-bold'>Pin</span>{t('pinMap:titleRight')}
          </Typography>
        </div>
        <MapBox />
        <div className='w-full h-auto flex justify-center items-center my-14'>
          <Button variant="contained" className='w-72 h-14' style={isDisabled ? disabledStyle : buttonStyle} onClick={openModal} disabled={isDisabled}>
            {t('pinMap:share_button')}
          </Button>
        </div>
        <div className='flex flex-col items-center mt-4 px-4 sm:mt-6 mb-4 text-center'>
          <Typography variant='h4' className='text-text-primary font-medium text-xl sm:text-xl md:text-2xl mt-2'>
            {t('pinMap:subtitle')}
          </Typography>
          {localizedMarkers.length > 0 ? (
            <div className='grid grid-cols-1 gap-8 w-auto sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 lg:gap-20 w-full justify-center items-center my-8'>
              {localizedMarkers.map((marker) => (
                <Card
                  key={marker.id}
                  title={marker.localizedName || marker.name}
                  description={marker.content}
                  imgSrc={getStaticMapImageUrl(marker.longitude, marker.latitude)}
                  lastVisit={String(marker.date_last_visit)}
                  imgClassName='w-full h-full object-cover'
                />
              ))}
            </div>
          ) : (
            <div className='flex flex-col items-center mt-4 px-4 sm:mt-6 mb-4 text-center'>
              <p className="text-sm text-muted-foreground mb-4">{t('pinMap:no_data')}</p>
              <img src={EmptyState} alt='Empty state' style={{ width: '9rem', height: 'auto', objectFit: 'cover' }} />
            </div>
          )}
        </div>
      </div>

      <ModalShareMap
        isOpen={isModalOpen}
        onClose={closeModal}
        link={handleShareMap().mapLink}
        image={handleShareMap().staticImageUrl}
      />
    </>
  )
}

export default PinMap